import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeaderSkeleton, StatCardsSkeleton, CardGridSkeleton, TableSkeleton } from "@/components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Plus, ClipboardList, Send, Trash2, Edit, Eye, ChevronRight, CheckCircle2,
  FileText, AlertCircle, Users, Download, Bell, Lock, Unlock, Star,
  GripVertical, Save, X, BarChart3, MessageSquare, ShieldAlert, Shield,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Survey, SurveyDetail, SurveyQuestion, SurveyResults, SurveyQuestionResult,
  apiGetSurveys, apiGetSurvey, apiCreateSurvey, apiUpdateSurvey, apiDeleteSurvey,
  apiPublishSurvey, apiCloseSurvey,
  apiAddSurveyQuestion, apiUpdateSurveyQuestion, apiDeleteSurveyQuestion,
  apiGetSurveyResults, apiSendSurveyReminder, apiExportSurvey, getTenantSlug,
} from "@/lib/api";
import { SplitFormModal } from "@/components/ui/SplitFormModal";

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6"];
const QUESTION_TYPES = ["single_choice", "multiple_choice", "text", "rating"] as const;

const CASE_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  submitted: { label: "status_submitted", color: "bg-blue-100 text-blue-700" },
  in_review: { label: "status_in_review", color: "bg-yellow-100 text-yellow-700" },
  assigned:  { label: "status_assigned",  color: "bg-orange-100 text-orange-700" },
  resolved:  { label: "status_resolved",  color: "bg-green-100 text-green-700" },
  closed:    { label: "status_closed",    color: "bg-gray-100 text-gray-600" },
};
const CASE_STATUS_OPTIONS = ["submitted", "in_review", "assigned", "resolved", "closed"];

const statusConfig: Record<string, { color: string; label: string }> = {
  draft: { color: "bg-gray-100 text-gray-700", label: "Draft" },
  active: { color: "bg-green-100 text-green-700", label: "Active" },
  closed: { color: "bg-blue-100 text-blue-700", label: "Closed" },
};

const qTypeLabels: Record<string, string> = {
  single_choice: "Single Choice",
  multiple_choice: "Multiple Choice",
  text: "Free Text",
  rating: "Rating (1-5)",
};

export default function Surveys() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: surveys = [], isLoading: loading } = useQuery<Survey[]>({
    queryKey: ["surveys", getTenantSlug()],
    queryFn: apiGetSurveys,
  });

  // Controlled tab — defaults to URL ?tab= param
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");


  // Survey builder
  const [isPublishing, setIsPublishing] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState<SurveyDetail | null>(null);
  const [surveyForm, setSurveyForm] = useState({ title: "", description: "", isAnonymous: false, startDate: "", endDate: "" });
  const [newSurveyDialog, setNewSurveyDialog] = useState(false);

  // Auto-open create dialog when navigated from Quick Actions
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      setNewSurveyDialog(true);
    }
  }, [searchParams]);

  // Question dialog
  const [qDialog, setQDialog] = useState(false);
  const [editingQ, setEditingQ] = useState<SurveyQuestion | null>(null);
  const [qForm, setQForm] = useState({ questionText: "", questionType: "single_choice" as string, isRequired: true, options: [{ optionText: "" }, { optionText: "" }] });
  const [savingQuestion, setSavingQuestion] = useState(false);

  // Results
  const [resultsSheet, setResultsSheet] = useState(false);
  const [results, setResults] = useState<SurveyResults | null>(null);
  const [resultsSurvey, setResultsSurvey] = useState<Survey | null>(null);

  const loadData = () => qc.invalidateQueries({ queryKey: ["surveys", getTenantSlug()] });

  const handleCreateSurvey = async () => {
    if (!surveyForm.title) return;
    try {
      const created = await apiCreateSurvey(surveyForm);
      toast({ title: t("surveys.surveyCreated") });
      setNewSurveyDialog(false);
      setSurveyForm({ title: "", description: "", isAnonymous: false, startDate: "", endDate: "" });
      loadData();
      const full = await apiGetSurvey(created.id);
      setActiveSurvey(full);
      setBuilderOpen(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleCreateSurveyFromModal = async (data: Record<string, string>) => {
    const payload = {
      title: data.title,
      description: data.description || "",
      isAnonymous: data.isAnonymous === "yes",
      startDate: data.startDate || "",
      endDate: data.endDate || "",
    };
    try {
      const created = await apiCreateSurvey(payload);
      toast({ title: t("surveys.surveyCreated") });
      setNewSurveyDialog(false);
      loadData();
      const full = await apiGetSurvey(created.id);
      setSurveyForm({ title: full.title, description: full.description || "", isAnonymous: full.isAnonymous, startDate: full.startDate || "", endDate: full.endDate || "" });
      setActiveSurvey(full);
      setBuilderOpen(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
      throw e; // re-throw so SplitFormModal doesn't show success
    }
  };

  const openBuilder = async (survey: Survey) => {
    try {
      const full = await apiGetSurvey(survey.id);
      setActiveSurvey(full);
      setSurveyForm({ title: full.title, description: full.description || "", isAnonymous: full.isAnonymous, startDate: full.startDate || "", endDate: full.endDate || "" });
      setBuilderOpen(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSaveSurveyDetails = async () => {
    if (!activeSurvey) return;
    try {
      await apiUpdateSurvey(activeSurvey.id, surveyForm);
      const full = await apiGetSurvey(activeSurvey.id);
      setActiveSurvey(full);
      loadData();
      toast({ title: t("surveys.saved") });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handlePublish = async () => {
    if (!activeSurvey) return;
    setIsPublishing(true);
    try {
      const r = await apiPublishSurvey(activeSurvey.id);
      toast({ title: t("surveys.published"), description: `${r.notified} ${t("surveys.employeesNotified")}` });
      const full = await apiGetSurvey(activeSurvey.id);
      setActiveSurvey(full);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = async () => {
    if (!activeSurvey) return;
    try {
      await apiCloseSurvey(activeSurvey.id);
      toast({ title: t("surveys.closed") });
      const full = await apiGetSurvey(activeSurvey.id);
      setActiveSurvey(full);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiDeleteSurvey(id);
      toast({ title: t("surveys.deleted") });
      setBuilderOpen(false);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleRemind = async (id: number) => {
    try {
      const r = await apiSendSurveyReminder(id);
      toast({ title: t("surveys.reminderSent"), description: `${r.reminded} ${t("surveys.employeesReminded")}` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Question management
  const openQDialog = (q?: SurveyQuestion) => {
    if (q) {
      setEditingQ(q);
      setQForm({
        questionText: q.questionText,
        questionType: q.questionType,
        isRequired: q.isRequired,
        options: q.options.length > 0 ? q.options.map((o) => ({ optionText: o.optionText })) : [{ optionText: "" }, { optionText: "" }],
      });
    } else {
      setEditingQ(null);
      setQForm({ questionText: "", questionType: "single_choice", isRequired: true, options: [{ optionText: "" }, { optionText: "" }] });
    }
    setQDialog(true);
  };

  const handleSaveQuestion = async () => {
    if (!activeSurvey || !qForm.questionText || savingQuestion) return;
    const needsOptions = ["single_choice", "multiple_choice"].includes(qForm.questionType);
    const opts = needsOptions ? qForm.options.filter((o) => o.optionText.trim()) : [];
    if (needsOptions && opts.length < 2) {
      toast({ title: t("surveys.minOptions"), variant: "destructive" });
      return;
    }
    setSavingQuestion(true);
    try {
      if (editingQ) {
        await apiUpdateSurveyQuestion(activeSurvey.id, editingQ.id, {
          questionText: qForm.questionText,
          questionType: qForm.questionType,
          isRequired: qForm.isRequired,
          options: needsOptions ? opts : undefined,
        });
      } else {
        await apiAddSurveyQuestion(activeSurvey.id, {
          questionText: qForm.questionText,
          questionType: qForm.questionType,
          isRequired: qForm.isRequired,
          sortOrder: activeSurvey.questions.length,
          options: needsOptions ? opts : undefined,
        });
      }
      const full = await apiGetSurvey(activeSurvey.id);
      setActiveSurvey(full);
      setQDialog(false);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (qId: number) => {
    if (!activeSurvey) return;
    try {
      await apiDeleteSurveyQuestion(activeSurvey.id, qId);
      const full = await apiGetSurvey(activeSurvey.id);
      setActiveSurvey(full);
      loadData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Results
  const openResults = async (survey: Survey) => {
    try {
      const r = await apiGetSurveyResults(survey.id);
      setResults(r);
      setResultsSurvey(survey);
      setResultsSheet(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Stats
  const activeSurveys = surveys.filter((s) => s.status === "active");
  const draftSurveys = surveys.filter((s) => s.status === "draft");
  const closedSurveys = surveys.filter((s) => s.status === "closed");
  const totalResponses = surveys.reduce((acc, s) => acc + s.responseCount, 0);
  const avgParticipation = surveys.length > 0
    ? Math.round(surveys.filter((s) => s.totalEmployees).reduce((a, s) => a + (s.totalEmployees ? (s.responseCount / s.totalEmployees) * 100 : 0), 0) / Math.max(surveys.filter((s) => s.totalEmployees).length, 1))
    : 0;

  const surveysByStatus = [
    { status: "active", label: t("surveys.activeSurveys"), value: activeSurveys.length },
    { status: "draft", label: statusConfig.draft.label, value: draftSurveys.length },
    { status: "closed", label: statusConfig.closed.label, value: closedSurveys.length },
  ].filter((s) => s.value > 0);

  const responsesBySurvey = [...surveys]
    .sort((a, b) => b.responseCount - a.responseCount)
    .slice(0, 8)
    .map((s) => ({
      name: s.title.length > 18 ? `${s.title.slice(0, 18)}…` : s.title,
      responses: s.responseCount,
    }));

  const recentSurveys = [...surveys]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <PageHeaderSkeleton />
          <StatCardsSkeleton count={4} />
          <CardGridSkeleton count={2} className="grid grid-cols-1 lg:grid-cols-2 gap-4" />
          <Card className="shadow-card">
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent>
              <TableSkeleton rows={5} cols={4} />
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
              <ClipboardList className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("surveys.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("surveys.subtitle")}</p>
            </div>
          </div>
          <Button onClick={() => { setSurveyForm({ title: "", description: "", isAnonymous: false, startDate: "", endDate: "" }); setNewSurveyDialog(true); }}>
            <Plus className="h-4 w-4 mr-2" />{t("surveys.newSurvey")}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} id="surveys-tabs">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="overview"
              id="tab-surveys-overview"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              {t("surveys.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger
              value="list"
              id="tab-surveys-list"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              {t("surveys.title")}
            </TabsTrigger>
          </TabsList>

          {/* ── Overview Tab ── */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* KPI cards */}
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0 lg:divide-y-0">
                <div className="flex items-center gap-3.5 px-5 py-4">
                  <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                    <ClipboardList className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-foreground leading-none">{surveys.length}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.totalSurveys")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3.5 px-5 py-4">
                  <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <Send className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-foreground leading-none">{activeSurveys.length}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.activeSurveys")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3.5 px-5 py-4">
                  <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-foreground leading-none">{totalResponses}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.totalResponses")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3.5 px-5 py-4">
                  <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                    <BarChart3 className="h-4 w-4 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-foreground leading-none">{surveys.length > 0 ? `${avgParticipation}%` : "—"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.avgParticipation")}</p>
                  </div>
                </div>
              </div>
            </div>

            {surveys.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground border border-dashed rounded-2xl bg-muted/20">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p>{t("surveys.noSurveys")}</p>
              </div>
            ) : (
              <>
                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{t("surveys.overview.byStatus")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {surveysByStatus.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">{t("surveys.overview.noData")}</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie
                              data={surveysByStatus}
                              dataKey="value"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              label={({ label, value }) => `${label}: ${value}`}
                            >
                              {surveysByStatus.map((_, idx) => (
                                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{t("surveys.overview.responsesPerSurvey")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {responsesBySurvey.every((r) => r.responses === 0) ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">{t("surveys.overview.noData")}</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={responsesBySurvey}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="responses" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent surveys */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t("surveys.overview.recentSurveys")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-xl border border-border/60 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("surveys.surveyTitle")}</TableHead>
                            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("surveys.title")}</TableHead>
                            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("surveys.participation")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recentSurveys.map((s) => {
                            const sc = statusConfig[s.status] || statusConfig.draft;
                            const participation = s.totalEmployees ? Math.round((s.responseCount / s.totalEmployees) * 100) : 0;
                            return (
                              <TableRow key={s.id} className="border-b border-border/40 last:border-0">
                                <TableCell className="pl-4 py-3 font-medium text-sm">{s.title}</TableCell>
                                <TableCell className="py-3">
                                  <Badge className={`${sc.color} text-xs border-0`}>{sc.label}</Badge>
                                </TableCell>
                                <TableCell className="py-3">
                                  {s.status !== "draft" && s.totalEmployees ? (
                                    <div className="flex items-center gap-2">
                                      <Progress value={participation} className="w-20 h-2" />
                                      <span className="text-xs font-mono text-muted-foreground">{participation}%</span>
                                      <span className="text-xs text-muted-foreground">({s.responseCount}/{s.totalEmployees})</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{s.responseCount} {t("surveys.responses")}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ── Surveys List Tab ── */}
          <TabsContent value="list" className="mt-6 space-y-6">
        {/* Stats strip */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0 lg:divide-y-0">
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                <ClipboardList className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{surveys.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.totalSurveys")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Send className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{activeSurveys.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.activeSurveys")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <MessageSquare className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{totalResponses}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.totalResponses")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                <BarChart3 className="h-4 w-4 text-violet-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">
                  {surveys.length > 0
                    ? `${Math.round(surveys.filter((s) => s.totalEmployees).reduce((a, s) => a + (s.totalEmployees ? (s.responseCount / s.totalEmployees) * 100 : 0), 0) / Math.max(surveys.filter((s) => s.totalEmployees).length, 1))}%`
                    : "—"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("surveys.avgParticipation")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Survey list */}
        {surveys.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground border border-dashed rounded-2xl bg-muted/20">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p>{t("surveys.noSurveys")}</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                  <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Survey</TableHead>
                  <TableHead className="hidden md:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Questions</TableHead>
                  <TableHead className="hidden lg:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Participation</TableHead>
                  <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Status</TableHead>
                  <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Actions</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {surveys.map((s) => {
                  const sc = statusConfig[s.status] || statusConfig.draft;
                  const participation = s.totalEmployees ? Math.round((s.responseCount / s.totalEmployees) * 100) : 0;
                  return (
                    <TableRow
                      key={s.id}
                      className="border-b border-border/40 transition-colors cursor-pointer hover:bg-muted/40"
                      onClick={() => s.responseCount > 0 ? openResults(s) : openBuilder(s)}
                    >
                      <TableCell className="pl-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                            <ClipboardList className="h-4 w-4 text-violet-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{s.title}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {s.isAnonymous && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                  <Lock className="h-2.5 w-2.5" />{t("surveys.anonymous")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell py-3 text-sm text-muted-foreground">
                        {s.questionCount} {t("surveys.questions")}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell py-3">
                        {s.status !== "draft" && s.totalEmployees ? (
                          <div className="flex items-center gap-2">
                            <Progress value={participation} className="w-20 h-2" />
                            <span className="text-xs font-mono text-muted-foreground">{participation}%</span>
                            <span className="text-xs text-muted-foreground">({s.responseCount}/{s.totalEmployees})</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">{s.responseCount} {t("surveys.responses")}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge className={`${sc.color} text-xs border-0`}>{sc.label}</Badge>
                      </TableCell>
                      <TableCell className="py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 items-center">
                          {s.status === "active" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" title={t("surveys.remind")} onClick={() => handleRemind(s.id)}>
                              <Bell className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {s.responseCount > 0 && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" title={t("surveys.viewResults")} onClick={() => openResults(s)}>
                              <BarChart3 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {s.responseCount > 0 && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" title={t("surveys.export")} onClick={() => apiExportSurvey(s.id, s.title).catch(() => toast({ title: t("surveys.exportFailed"), variant: "destructive" }))}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={t("surveys.edit")} onClick={() => openBuilder(s)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 pr-3">
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
          </TabsContent>

        </Tabs>
      </div>

      {/* ── New Survey Dialog ── */}
      <SplitFormModal
        open={newSurveyDialog}
        onOpenChange={setNewSurveyDialog}
        badge="Surveys"
        heading="Create a New Survey"
        subheading="Gather honest feedback from your entire team in minutes."
        benefits={[
          "Anonymous or named responses",
          "Real-time participation tracking",
          "Export results to CSV",
        ]}
        brandGradient="from-violet-600 via-purple-600 to-indigo-700"
        formHeading="Survey details"
        formSubheading="Fill in the basics — you'll add questions next."
        submitLabel={t("surveys.createAndBuild")}
        successMessage="Survey created! Opening the builder now…"
        fields={[
          { name: "title", label: t("surveys.surveyTitle"), type: "text", placeholder: "e.g. Q2 Employee Satisfaction", required: true },
          { name: "description", label: t("surveys.description"), type: "textarea", placeholder: "Optional context for respondents…" },
          { name: "isAnonymous", label: t("surveys.anonymousResponses"), type: "select", options: ["yes", "no"], placeholder: "No (named responses)" },
          { name: "startDate", label: t("surveys.startDate"), type: "date", half: true },
          { name: "endDate", label: t("surveys.endDate"), type: "date", half: true },
        ]}
        onSubmit={handleCreateSurveyFromModal}
      />

      {/* ── Survey Builder Dialog ── */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              {activeSurvey?.title}
              {activeSurvey && <Badge className={statusConfig[activeSurvey.status]?.color}>{statusConfig[activeSurvey.status]?.label}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
            {activeSurvey && (
              <div className="space-y-6 pb-6">
                {/* Survey details */}
                <div className="space-y-3 p-4 rounded-lg border bg-secondary/30">
                  <div><Label>{t("surveys.surveyTitle")}</Label><Input value={surveyForm.title} onChange={(e) => setSurveyForm({ ...surveyForm, title: e.target.value })} /></div>
                  <div><Label>{t("surveys.description")}</Label><Textarea value={surveyForm.description} onChange={(e) => setSurveyForm({ ...surveyForm, description: e.target.value })} className="min-h-[60px]" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("surveys.startDate")}</Label><DatePicker value={surveyForm.startDate} onChange={(v) => setSurveyForm({ ...surveyForm, startDate: v })} className="w-full mt-1" /></div>
                    <div><Label>{t("surveys.endDate")}</Label><DatePicker value={surveyForm.endDate} onChange={(v) => setSurveyForm({ ...surveyForm, endDate: v })} className="w-full mt-1" /></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch checked={surveyForm.isAnonymous} onCheckedChange={(v) => setSurveyForm({ ...surveyForm, isAnonymous: v })} disabled={activeSurvey.status !== "draft"} />
                      <Label>{t("surveys.anonymousResponses")}</Label>
                    </div>
                    <Button size="sm" onClick={handleSaveSurveyDetails}><Save className="h-3.5 w-3.5 mr-1" />{t("common.save")}</Button>
                  </div>
                </div>

                {/* Questions */}
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-semibold">{t("surveys.questions")} ({activeSurvey.questions.length})</h4>
                  <Button size="sm" variant="outline" onClick={() => openQDialog()} disabled={activeSurvey.status === "closed"}>
                    <Plus className="h-3 w-3 mr-1" />{t("surveys.addQuestion")}
                  </Button>
                </div>

                <div className="space-y-3">
                  {activeSurvey.questions.map((q, idx) => (
                    <div key={q.id} className="p-4 rounded-lg border bg-card hover:shadow-sm transition-all group">
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-bold text-muted-foreground mt-0.5">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{q.questionText}</p>
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px]">{qTypeLabels[q.questionType]}</Badge>
                            {q.isRequired && <Badge variant="destructive" className="text-[10px]">Required</Badge>}
                          </div>
                          {q.options.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {q.options.map((o) => (
                                <div key={o.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {q.questionType === "single_choice" ? (
                                    <div className="h-3 w-3 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                                  ) : (
                                    <div className="h-3 w-3 rounded border-2 border-muted-foreground/30 shrink-0" />
                                  )}
                                  {o.optionText}
                                </div>
                              ))}
                            </div>
                          )}
                          {q.questionType === "rating" && (
                            <div className="flex gap-1 mt-2">
                              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-4 w-4 text-amber-300" />)}
                            </div>
                          )}
                        </div>
                        {activeSurvey.status !== "closed" && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openQDialog(q)}><Edit className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteQuestion(q.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {activeSurvey.questions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">{t("surveys.noQuestions")}</div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t flex-wrap">
                  {activeSurvey.status === "draft" && (
                    <Button onClick={handlePublish} disabled={activeSurvey.questions.length === 0 || isPublishing} className="gap-1.5">
                      <Send className="h-4 w-4" />{isPublishing ? t("common.saving") : t("surveys.publish")}
                    </Button>
                  )}
                  {activeSurvey.status === "active" && (
                    <>
                      <Button variant="outline" onClick={handleClose} className="gap-1.5">
                        <Lock className="h-4 w-4" />{t("surveys.closeSurvey")}
                      </Button>
                      <Button variant="outline" onClick={() => handleRemind(activeSurvey.id)} className="gap-1.5">
                        <Bell className="h-4 w-4" />{t("surveys.sendReminder")}
                      </Button>
                    </>
                  )}
                  {activeSurvey.responseCount > 0 && (
                    <>
                      <Button variant="outline" onClick={() => openResults({ ...activeSurvey, questionCount: activeSurvey.questions.length } as any)} className="gap-1.5">
                        <BarChart3 className="h-4 w-4" />{t("surveys.viewResults")}
                      </Button>
                      <Button variant="outline" className="gap-1.5" onClick={() => apiExportSurvey(activeSurvey.id, activeSurvey.title).catch(() => toast({ title: t("surveys.exportFailed"), variant: "destructive" }))}>
                        <Download className="h-4 w-4" />{t("surveys.exportCSV")}
                      </Button>
                    </>
                  )}
                  <div className="flex-1" />
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(activeSurvey.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />{t("surveys.deleteSurvey")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Question Dialog ── */}
      <Dialog open={qDialog} onOpenChange={(open) => { setQDialog(open); if (!open) setEditingQ(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingQ ? t("surveys.editQuestion") : t("surveys.addQuestion")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("surveys.questionText")}</Label><Textarea value={qForm.questionText} onChange={(e) => setQForm({ ...qForm, questionText: e.target.value })} className="min-h-[60px]" /></div>
            <div>
              <Label>{t("surveys.questionType")}</Label>
              <Select value={qForm.questionType} onValueChange={(v) => setQForm({ ...qForm, questionType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((qt) => <SelectItem key={qt} value={qt}>{qTypeLabels[qt]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {["single_choice", "multiple_choice"].includes(qForm.questionType) && (
              <div className="space-y-2">
                <Label>{t("surveys.options")}</Label>
                {qForm.options.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder={`${t("surveys.option")} ${i + 1}`}
                      value={opt.optionText}
                      onChange={(e) => {
                        const newOpts = [...qForm.options];
                        newOpts[i] = { optionText: e.target.value };
                        setQForm({ ...qForm, options: newOpts });
                      }}
                    />
                    {qForm.options.length > 2 && (
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setQForm({ ...qForm, options: qForm.options.filter((_, j) => j !== i) })}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setQForm({ ...qForm, options: [...qForm.options, { optionText: "" }] })}>
                  <Plus className="h-3 w-3 mr-1" />{t("surveys.addOption")}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={qForm.isRequired} onCheckedChange={(v) => setQForm({ ...qForm, isRequired: v })} />
              <Label>{t("surveys.required")}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveQuestion} disabled={!qForm.questionText || savingQuestion}>
              {savingQuestion ? <><span className="h-3.5 w-3.5 mr-1.5 animate-spin rounded-full border-2 border-current border-t-transparent inline-block" />{t("common.saving")}</> : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Results Dialog ── */}
      <Dialog open={resultsSheet} onOpenChange={setResultsSheet}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {resultsSurvey?.title} — {t("surveys.results")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {results && (
              <div className="space-y-6 pb-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{results.responseCount}</p>
                    <p className="text-xs text-muted-foreground">{t("surveys.responses")}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{results.totalEmployees}</p>
                    <p className="text-xs text-muted-foreground">{t("surveys.totalInvited")}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold">{results.totalEmployees > 0 ? Math.round((results.responseCount / results.totalEmployees) * 100) : 0}%</p>
                    <p className="text-xs text-muted-foreground">{t("surveys.participation")}</p>
                  </CardContent></Card>
                </div>

                {results.isAnonymous && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    <Lock className="h-4 w-4 shrink-0" />
                    {t("surveys.anonymousNote")}
                  </div>
                )}

                {/* Question results */}
                {results.results.map((qr, idx) => (
                  <Card key={qr.questionId}>
                    <CardContent className="p-5 space-y-3">
                      <p className="text-sm font-semibold">{idx + 1}. {qr.questionText}</p>

                      {(qr.questionType === "single_choice" || qr.questionType === "multiple_choice") && qr.optionCounts && (
                        <>
                          <ResponsiveContainer width="100%" height={Math.max(120, (qr.optionCounts.length) * 40)}>
                            <BarChart data={qr.optionCounts} layout="vertical" margin={{ left: 10, right: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis type="category" dataKey="optionText" width={150} tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                          <p className="text-xs text-muted-foreground">{qr.totalResponses} {t("surveys.responses")}</p>
                        </>
                      )}

                      {qr.questionType === "text" && qr.textAnswers && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {qr.textAnswers.map((ta, i) => (
                            <div key={i} className="p-2.5 rounded-lg bg-secondary/50 text-sm">
                              {ta.text}
                              {ta.respondentName && <span className="text-xs text-muted-foreground block mt-1">— {ta.respondentName}</span>}
                            </div>
                          ))}
                          {qr.textAnswers.length === 0 && <p className="text-sm text-muted-foreground">{t("surveys.noAnswers")}</p>}
                        </div>
                      )}

                      {qr.questionType === "rating" && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} className={`h-5 w-5 ${n <= Math.round(qr.avgRating || 0) ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
                              ))}
                            </div>
                            <span className="text-lg font-bold">{(qr.avgRating || 0).toFixed(1)}</span>
                            <span className="text-xs text-muted-foreground">({qr.ratingCount} {t("surveys.responses")})</span>
                          </div>
                          {qr.ratingDistribution && (
                            <div className="space-y-1">
                              {[5, 4, 3, 2, 1].map((r) => {
                                const d = qr.ratingDistribution?.find((x) => x.rating === r);
                                const count = d?.count || 0;
                                const pct = qr.ratingCount ? (count / qr.ratingCount) * 100 : 0;
                                return (
                                  <div key={r} className="flex items-center gap-2 text-xs">
                                    <span className="w-4 text-right">{r}</span>
                                    <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                                    <div className="flex-1 bg-secondary rounded-full h-2 overflow-hidden">
                                      <div className="bg-amber-400 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="w-6 text-right text-muted-foreground">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Respondent list with answers (non-anonymous) */}
                {!results.isAnonymous && results.respondents.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />{t("surveys.respondentList")}</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {results.respondents.map((r) => (
                        <RespondentCard key={r.responseId} respondent={r} />
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="pt-4 border-t">
                  <Button variant="outline" className="gap-1.5" onClick={() => apiExportSurvey(results.surveyId).catch(() => toast({ title: t("surveys.exportFailed"), variant: "destructive" }))}>
                    <Download className="h-4 w-4" />{t("surveys.exportCSV")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


    </AppLayout>
  );
}

// ── RespondentCard ────────────────────────────────────────────────────────────
function RespondentCard({ respondent }: {
  respondent: {
    responseId: number;
    employeeName: string;
    department: string;
    submittedAt: string;
    answers: {
      questionId: number;
      questionText: string;
      questionType: string;
      textAnswer: string | null;
      ratingValue: number | null;
      chosenOptions: string | null;
    }[];
  };
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-sm hover:bg-muted/50 transition-colors text-left"
      >
        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
        <span className="font-medium flex-1">{respondent.employeeName || "—"}</span>
        <span className="text-muted-foreground text-xs">{respondent.department}</span>
        <span className="text-muted-foreground text-xs ml-4">{new Date(respondent.submittedAt).toLocaleDateString()}</span>
        <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-border bg-muted/20 divide-y divide-border">
          {respondent.answers.length === 0 && (
            <p className="px-4 py-3 text-xs text-muted-foreground italic">No answers recorded.</p>
          )}
          {respondent.answers.map((a) => (
            <div key={a.questionId} className="px-4 py-3 space-y-0.5">
              <p className="text-xs font-medium text-muted-foreground">{a.questionText}</p>
              {a.questionType === "text" && (
                <p className="text-sm">{a.textAnswer || <span className="italic text-muted-foreground">No answer</span>}</p>
              )}
              {(a.questionType === "single_choice" || a.questionType === "multiple_choice") && (
                <p className="text-sm">{a.chosenOptions || <span className="italic text-muted-foreground">No answer</span>}</p>
              )}
              {a.questionType === "rating" && a.ratingValue != null && (
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-4 w-4 ${n <= a.ratingValue! ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
                  ))}
                  <span className="text-sm ml-1 text-muted-foreground">{a.ratingValue}/5</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
